import { computed, ref } from 'vue';
import { useTask } from '@/ui/concerns';
import { all as getData } from '@/services/api/resources/analytics/actions';
import { ReportFilter, ReportWidget } from '@/services/api/resources/analytics-builder/model';
import {
  AllAttributesType,
  OptionalAttrsType,
  DefaultAttrsType,
  FilterType,
  MetricsType,
  PunctualityType,
} from '@/services/api/resources/analytics-builder/constants';
import { DetailedDataType, parseDetailedViewData } from '@/services/api/resources/analytics-builder/transform';
import { useWidgetFormItem, useWidgets } from '@/ui/concerns/analytics/widget/useWidgets';
import cloneDeep from 'lodash/cloneDeep';
import { DetailedViewOptions, DetailedViewType } from '@/stores/analytics/detailedView';
import { t } from '@/ui/i18n/i18n';
import { useAnalyticsMap } from '@/ui/concerns/analytics/useAnalyticsMap';
import { useAttributes } from '@/ui/concerns/analytics/widget';

export interface DetailedViewPayload {
  filters: ReportFilter;
  metric: MetricsType | null;
  attribute: AllAttributesType | null;
  page: number | undefined;
  perPage: number | undefined;
  searchTerm: string | undefined;
}

type DetailedViewResult = { items: unknown[]; totalRows: number };

export const useDetailedView = () => {
  const search = ref('');
  const page = ref(1);
  const perPage = 20;
  const data = ref<DetailedViewResult>({ items: [], totalRows: 0 });

  const getDetailedViewData = useTask(
    (payload: DetailedViewPayload | undefined) => getData({
      detailedView: true,
      filters: payload!.filters,
      metric: payload!.metric,
      attribute: payload!.attribute,
      page: payload!.page,
      perPage: payload!.perPage,
      display: null,
      lineChartTimeGrouping: null,
      searchTerm: payload!.searchTerm,
    }),
    response => {
      data.value = parseDetailedViewData(
        response.parsed as DetailedDataType,
      );
    },
  );

  const getDetailedViewDataTask = computed(() => getDetailedViewData);

  const getDetailedViewType = (metric: MetricsType) : DetailedViewType => {
    switch (metric) {
      case 'schedule-forms':
        return 'schedule-forms';
      case 'actions':
        return 'actions';
      case 'form-items':
      case 'form-item-score':
        return 'form-items';
      case 'negative-responses':
        return 'negative-responses';
      default:
        return 'submitted-forms';
    }
  };

  const getDetailedViewTitle = (detailedViewType: DetailedViewType, value: number | string, widget: ReportWidget) => {
    if (!widget.attribute) {
      return '';
    }

    const { isAllChartsDisabled } = useAnalyticsMap();
    const { isResponseAttributeSelected, isDownloadable } = useWidgetFormItem(widget);
    const { allAttributeTypeLabels } = useAttributes();
    const title = `${t(`analytics.detailed-view.${detailedViewType}`)} ${t('analytics.by')} ${allAttributeTypeLabels[widget.attribute]}`;

    if ((isResponseAttributeSelected.value && isDownloadable.value)
      || isAllChartsDisabled(widget.metric, widget.attribute)) {
      return title;
    }

    return `${title} ${value}`;
  };

  return {
    search,
    page,
    perPage,
    getDetailedViewDataTask,
    data,
    getDetailedViewType,
    getDetailedViewTitle,
  };
};

export const useDetailedViewFilters = () => {
  const { combineFilters } = useWidgets();
  const numberArrayDefaultAttributes: DefaultAttrsType[] = ['folders', 'entities', 'groups', 'users', 'categories',
    'templates'];
  const dateFrameDefaultAttributes: DefaultAttrsType[] = ['day', 'week', 'month', 'quarter', 'year'];
  const numberDefaultAttributes: OptionalAttrsType[] = ['punctuality'];
  const numberArrayOptionalAttributes: OptionalAttrsType[] = ['form-status', 'action-status', 'creator',
    'approval-status'];
  const stringArrayOptionalAttributes: OptionalAttrsType[] = ['page-section'];
  const stringOptionalAttributes: OptionalAttrsType[] = ['form-item', 'response'];
  const numberOptionalAttributes: OptionalAttrsType[] = ['priority'];

  type numberArrayDefaultFilters = FilterType.folder | FilterType.entity | FilterType.group | FilterType.user
  | FilterType.category | FilterType.checklist;
  type numberArrayOptionalFilters = FilterType.formStatus | FilterType.actionStatus | FilterType.creator
  | FilterType.approvalStatus | FilterType.approvers;
  type stringOptionalFilters = FilterType.formItem | FilterType.response;

  const getNumberArrayDefaultFilter = (attribute: AllAttributesType): numberArrayDefaultFilters | null => {
    switch (attribute) {
      case 'folders':
        return FilterType.folder;
      case 'entities':
        return FilterType.entity;
      case 'groups':
        return FilterType.group;
      case 'users':
        return FilterType.user;
      case 'categories':
        return FilterType.category;
      case 'templates':
        return FilterType.checklist;
      default:
        return null;
    }
  };

  const getNumberArrayOptionalFilter = (attribute: AllAttributesType): numberArrayOptionalFilters | null => {
    switch (attribute) {
      case 'form-status':
        return FilterType.formStatus;
      case 'action-status':
        return FilterType.actionStatus;
      case 'creator':
        return FilterType.creator;
      case 'approval-status':
        return FilterType.approvalStatus;
      case 'approvers':
        return FilterType.approvers;
      default:
        return null;
    }
  };

  const getStringOptionalFilter = (attribute: AllAttributesType): stringOptionalFilters | null => {
    switch (attribute) {
      case 'form-item':
        return FilterType.formItem;
      case 'response':
        return FilterType.response;
      default:
        return null;
    }
  };

  const prepareFilters = (
    widgetFilter: ReportFilter,
    reportFilter: ReportFilter,
    attribute: AllAttributesType | null,
    resourceId: number | string | undefined,
    options: DetailedViewOptions,
  ) => {
    const filters = cloneDeep(combineFilters(widgetFilter, reportFilter));
    if (!attribute) {
      return filters;
    }

    if (numberArrayDefaultAttributes.includes(attribute as DefaultAttrsType)) {
      const filter = getNumberArrayDefaultFilter(attribute);
      if (filter !== null) {
        filters[filter].values = [Number(resourceId)];
      }
    }

    if (dateFrameDefaultAttributes.includes(attribute as DefaultAttrsType)) {
      filters.date.timeFrame = attribute;
      filters.date.timeFrameValue = resourceId as string;
    }

    if (numberDefaultAttributes.includes(attribute as OptionalAttrsType)) {
      filters.punctuality = Number(resourceId ? PunctualityType.tooLate : PunctualityType.inTime);
    }

    if (numberArrayOptionalAttributes.includes(attribute as OptionalAttrsType)) {
      const filter = getNumberArrayOptionalFilter(attribute);
      if (filter !== null) {
        if (filter in filters && filters[filter] !== undefined) {
          filters[filter]!.values = [Number(resourceId)];
        } else {
          filters[filter] = { values: [Number(resourceId)] };
        }
      }
    }

    if (stringArrayOptionalAttributes.includes(attribute as OptionalAttrsType)) {
      filters.section = { values: [String(resourceId)] };
    }

    if (stringOptionalAttributes.includes(attribute as OptionalAttrsType)) {
      const filter = getStringOptionalFilter(attribute);

      switch (filter) {
        case FilterType.formItem:
          filters.formItem = String(resourceId);
          break;
        case FilterType.response:
          filters.formItem = String(resourceId);
          filters.response = options.head as string;
          break;
        default:
          break;
      }
    }

    if (numberOptionalAttributes.includes(attribute as OptionalAttrsType)) {
      filters.priority = Number(resourceId);
    }

    return filters;
  };

  return {
    prepareFilters,
  };
};
